<div class="dash-shell submission">
  <header class="page-head">
    <nav class="page-head__crumbs" aria-label="Breadcrumb">
      <a class="page-head__crumb" routerLink="/dashboard">Home</a>
      <span class="page-head__sep" aria-hidden="true">/</span>
      @if (preApp(); as pa) {
        <a class="page-head__crumb" [routerLink]="['/entity', pa.entity_short_id, 'dashboard']">{{
          pa.entity_name
        }}</a>
        <span class="page-head__sep" aria-hidden="true">/</span>
      }
      <span class="page-head__crumb page-head__crumb--current" aria-current="page">Submission</span>
    </nav>
    <div class="page-head__bar">
      <div class="page-head__titles page-head__titles--icon">
        <span class="page-head__icon" aria-hidden="true"
          ><app-icon name="file-text" [size]="24"
        /></span>
        <h1 class="page-head__title">Your funding enquiry</h1>
        <p class="page-head__subtitle">What you shared with CapX, and where it stands.</p>
        @if (preApp()?.dealt_with_by; as name) {
          <p class="page-head__subtitle" data-testid="dealt-with-by">
            Dealt with at CapX by {{ name }}
          </p>
        }
      </div>
      @if (preApp(); as pa) {
        <div class="page-head__actions">
          <app-status-chip [status]="pa.review_status" data-testid="submission-status-chip" />
        </div>
      }
    </div>
  </header>

  @if (isLoading()) {
    <div class="loading-state" aria-label="Loading submission" data-testid="submission-loading">
      <span class="sg-skeleton sg-skeleton--heading sg-skeleton--w40"></span>
      <span class="sg-skeleton sg-skeleton--text" style="width: 25%"></span>
      <mat-card appearance="outlined" style="margin-top: var(--space-6)">
        <mat-card-content>
          <span
            class="sg-skeleton sg-skeleton--text"
            style="width: 30%; margin-bottom: var(--space-4); display: block"
          ></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w100"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w80"></span>
          <span class="sg-skeleton sg-skeleton--text sg-skeleton--w60"></span>
        </mat-card-content>
      </mat-card>
    </div>
  } @else if (denied()) {
    <!-- 🚨 Deliberately vague, and it must stay that way. This renders for BOTH
         "no such enquiry" and "someone else's enquiry", because the API refuses
         to distinguish them (applications/views.py:173). Naming which one it is
         would confirm a record exists to whoever is guessing short_ids. No
         retry button either — neither case can succeed on a second attempt. -->
    <div class="sg-empty sg-empty--bordered" role="alert" data-testid="submission-denied">
      <app-icon name="shield" [size]="40" />
      <p class="sg-empty__title">We couldn't find that enquiry</p>
      <p class="sg-empty__desc">
        It may have been removed, or it belongs to a business you don't have access to. Your own
        enquiries are on your dashboard.
      </p>
      <a mat-flat-button color="primary" [routerLink]="['/entity', entityId, 'dashboard']">
        Back to dashboard
      </a>
    </div>
  } @else if (loadError()) {
    <div class="sg-empty sg-empty--error sg-empty--bordered" data-testid="submission-error">
      <app-icon name="x-circle" [size]="40" />
      <p class="sg-empty__title">Couldn't load this submission</p>
      <p class="sg-empty__desc">Something went wrong on our side.</p>
      <button mat-stroked-button type="button" (click)="load()">Try again</button>
    </div>
  } @else if (preApp(); as pa) {
    <!-- Enquiry details — first under the heading (FLOW-394, 2026-10-22): what the
         client shared leads; where it stands and what happens next follow. -->
    <mat-card appearance="outlined" class="submission-card">
      <mat-card-content>
        <div class="submission-card__head">
          <h2 class="submission-card__title">Enquiry details</h2>
        </div>
        <dl class="facts">
          <div class="facts__row">
            <dt>Business</dt>
            <dd>{{ pa.entity_name }}</dd>
          </div>
          <div class="facts__row">
            <dt>Submitted</dt>
            <dd>{{ pa.submitted_at | date: 'medium' | dash }}</dd>
          </div>
          <div class="facts__row">
            <dt>Invoices on credit</dt>
            <dd>{{ pa.df_intent ? 'Yes' : 'No' }}</dd>
          </div>
          <div class="facts__row">
            <dt>Documents shared</dt>
            <dd>{{ documents().length }}</dd>
          </div>
        </dl>
      </mat-card-content>
    </mat-card>

    <!-- Where it stands. The outcome copy is the client's "why" — the internal
         routing enum is N4 and never appears on this screen. -->
    @if (outcome(); as o) {
      <mat-card appearance="outlined" class="submission-card" data-testid="submission-outcome">
        <mat-card-content>
          <div class="submission-card__head">
            <h2 class="submission-card__title">{{ o.headline }}</h2>
          </div>
          <p class="submission-card__body">{{ o.reason }}</p>
        </mat-card-content>
      </mat-card>
    }

    <!-- What happens next — same register as the dashboard banner it came from. -->
    <mat-card appearance="outlined" class="submission-card" data-testid="submission-next">
      <mat-card-content>
        <div class="submission-card__head">
          <h2 class="submission-card__title">What happens next</h2>
        </div>
        <!-- §A1.4 (FLOW-192 outcome 2): a referred enquiry is a person's, not the
             system's — no review promise, no product, no figure. -->
        @if (outcome()?.referred) {
          <p class="submission-card__body" data-testid="submission-referred">
            A person at CapX is handling this enquiry and will be in touch to help you take it
            forward.
          </p>
        } @else {
          <p class="submission-card__body">
            We're looking at what you shared and will be in touch with your funding options.
          </p>
        }
        <ul class="submission-facts">
          @if (outcome()?.referred) {
            <li>
              <app-icon name="users" [size]="16" />
              <span>Handled by a person at CapX</span>
            </li>
          } @else {
            <li>
              <app-icon name="clock" [size]="16" />
              <span>We review within one business day</span>
            </li>
          }
          @if (pa.df_intent) {
            <li data-testid="submission-df-fact">
              <app-icon name="zap" [size]="16" />
              <span>Debtors Finance interest recorded</span>
            </li>
          }
        </ul>
        @if (pa.client_decision_reason) {
          <p class="submission-card__reason" data-testid="submission-client-reason">
            {{ pa.client_decision_reason }}
          </p>
        }
      </mat-card-content>
    </mat-card>

    <!-- Documents, with the same verdict signal the client saw while uploading. -->
    <mat-card appearance="outlined" class="submission-card" data-testid="submission-documents">
      <mat-card-content>
        <div class="submission-card__head">
          <h2 class="submission-card__title">Documents you shared</h2>
        </div>
        @if (documents().length === 0) {
          <p class="submission-card__hint">
            No documents were shared. We may ask for some to complete the review.
          </p>
        } @else {
          @for (doc of documents(); track doc.short_id) {
            <div class="submission-doc">
              <app-doc-verdict
                [fileName]="doc.file_name"
                [status]="doc.assess_status"
                [message]="doc.assess_message"
              />
              <p class="submission-doc__meta">
                {{ DOC_TYPE_LABELS[doc.doc_type] }} · uploaded
                {{ doc.created_at | date: 'mediumDate' }}
              </p>
            </div>
          }
        }
      </mat-card-content>
    </mat-card>

    <!-- DOC-89 §4 (FLOW-391): the shared context blocks begin on the enquiry
         and are the same blocks on the application it becomes. -->
    <mat-card appearance="outlined" class="submission-card" data-testid="submission-context-blocks">
      <mat-card-content>
        <app-context-blocks-client subjectKind="PRE_APPLICATION" [subjectId]="pa.short_id" />
      </mat-card-content>
    </mat-card>

    <!--
      The declared lists (FLOW-281) — funders, related businesses, directors.
      Entity-level facts, not fields of this pre-application: declaring one
      cannot re-open the submitted record, which is why this screen's
      no-edit-affordances rule does not reach them. Editable while the review is
      still running; from PROCEEDED the Kickstart client detail is the live
      surface. Client mode ONLY — the accounts section is Ops-gated because the
      client declares accounts in the wizard's bank step, and a second list here
      would be a second place to declare the same thing.
    -->
    <app-declarations-panel
      [entityShortId]="pa.entity_short_id"
      mode="client"
      [readOnly]="declarationsReadOnly()"
    />

    <div class="submission-actions">
      <a
        mat-stroked-button
        [routerLink]="['/entity', entityId, 'dashboard']"
        data-testid="submission-dashboard-link"
      >
        Back to dashboard
      </a>
    </div>
  }
</div>
